<script setup lang="ts">
import { pb } from "@/pocketbase";
import { getTechnologyCategoryLabelForTechnologyType, getTechnologyTypeLabel, Product, ProductTechnology, Technology, technologyTypesByCategory } from "@/model";
import { computed, Ref, ref } from "vue";

import Input from "@/components/Input.vue";
import Listbox from "@/components/Listbox.vue";
import Title from "@/components/Title.vue";
import Heading from "@/components/Heading.vue";
import Heading2 from "@/components/Heading2.vue";
import Divider from "@/components/Divider.vue";
import Content from "@/components/Content.vue";

const searchTerm = ref('');

const products: Ref<Product[]> = ref([]);
const productTechnologies: Ref<ProductTechnology[]> = ref([]);

async function loadData() {
  products.value = await pb.collection('products').getFullList<Product>(null, {
    expand: 'businessUnit',
    sort: 'name',
    '$autoCancel': false,
  });

  productTechnologies.value = await pb.collection('product_technologies').getFullList<ProductTechnology>(null, {
    expand: 'product,technology,product.businessUnit',
    sort: 'technology.name',
    '$autoCancel': false,
  });
}

await loadData();

const businessUnitOptions = [{ key: '', label: 'All' }].concat(
  Array.from(new Map(products.value.map(product => [
    product.expand!.businessUnit!.id,
    {
      key: product.expand!.businessUnit!.id,
      label: product.expand!.businessUnit!.name
    }
  ])).values())
  .sort((a, b) => a.label.localeCompare(b.label))
);
const selectedBusinessUnit = ref(businessUnitOptions[0]);

type TechnologyCategoryUsage = {
  categoryLabel: string,
  types: TechnologyTypeUsage[],
};

type TechnologyTypeUsage = {
  typeLabel: string,
  technologies: TechnologyUsage[],
};

type TechnologyUsage = {
  technology: Technology,
  productTechnologies: ProductTechnology[],
};

const categories = computed(() => {
  let categoryUsages: TechnologyCategoryUsage[] = [];

  for (const productTechnology of productTechnologies.value) {
    if (searchTerm.value.length > 0 && !productTechnology.expand!.technology!.name.match(new RegExp(searchTerm.value, 'gi'))) {
      continue;
    }

    if (selectedBusinessUnit.value.key.length > 0 && selectedBusinessUnit.value.key !== productTechnology.expand!.product!.businessUnit) {
      continue;
    }

    const technology = productTechnology.expand!.technology!;
    const technologyTypeLabel = getTechnologyTypeLabel(technology.type);
    const technologyCategoryLabel = getTechnologyCategoryLabelForTechnologyType(technology.type);

    let categoryUsage = categoryUsages.find(u => u.categoryLabel === technologyCategoryLabel);
    if (!categoryUsage) {
      categoryUsage = {
        categoryLabel: technologyCategoryLabel,
        types: [],
      };
      categoryUsages.push(categoryUsage);

      // sort based on technologyTypesByCategory
      categoryUsages = categoryUsages.sort((a, b) => {
        const a1 = technologyTypesByCategory.indexOf(technologyTypesByCategory.find(({ category }) => category === a.categoryLabel));
        const b1 = technologyTypesByCategory.indexOf(technologyTypesByCategory.find(({ category }) => category === b.categoryLabel));

        return a1 - b1;
      })
    }

    let typeUsage = categoryUsage.types.find(u => u.typeLabel === technologyTypeLabel);
    if (!typeUsage) {
      typeUsage = {
        typeLabel: technologyTypeLabel,
        technologies: [],
      }
      categoryUsage.types.push(typeUsage);

      // sort based on technologyTypesByCategory
      const sortedTypes = technologyTypesByCategory.find(({ category }) => category === technologyCategoryLabel)?.types;
      categoryUsage.types = categoryUsage.types.sort((a, b) => {
        return sortedTypes.indexOf(a.typeLabel) - sortedTypes.indexOf(b.typeLabel);
      })
    }

    let technologyUsage = typeUsage.technologies.find(t => t.technology.id === technology.id);
    if (!technologyUsage) {
      technologyUsage = {
        technology,
        productTechnologies: [],
      }
      typeUsage.technologies = typeUsage.technologies
      .concat(technologyUsage)
      .sort((a, b) => a.technology.name.localeCompare(b.technology.name));
    }

    technologyUsage.productTechnologies = technologyUsage.productTechnologies
    .concat(productTechnology)
    .sort((a, b) => a.expand!.product!.name.localeCompare(b.expand!.product!.name));
  }

  return categoryUsages;
});

function getLogoUrl(technology: Technology): string {
  return pb.files.getUrl(technology, technology.logo);
}

const selectedTechnology: Ref<Technology | null> = ref(null);
</script>

<template>
  <Title subtitle="Technologies used by TSG business units">Tech Landscape</Title>
  <Divider/>
  <Content>
    <div class="grid md:grid-cols-5 gap-2 mb-4 lg:mb-12 items-center">
      <div class="md:col-span-2">
        <Input v-model="searchTerm" label="Search term" placeholder="Search technology ..." marginless/>
      </div>
      <div class="md:col-span-2">
        <Listbox label="Business Unit" optional v-model="selectedBusinessUnit" :options="businessUnitOptions" marginless/>
      </div>
    </div>

    <template v-for="({categoryLabel, types}) in categories">
      <Heading>{{ categoryLabel }}</Heading>
      <div v-for="({typeLabel, technologies}, idx) in types" :class="['flex flex-col sm:flex-row', idx > 0 ? 'border-t border-gray-200 dark:border-gray-700 pt-4' : '']">
        <div class="sm:basis-1/6 sm:pr-10 pb-2">
          <Heading2>{{ typeLabel }}</Heading2>
        </div>
        <div class="sm:basis-5/6">
          <div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <div v-for="usage in technologies" :class="[
                 selectedTechnology?.id === usage.technology.id ? 'col-span-3' : 'items-center',
                 'cursor-pointer p-2 sm:p-4 h-40 sm:h-48 rounded-lg border border-gray-300 dark:border-gray-600 flex justify-center',
               ]" @click="selectedTechnology = selectedTechnology?.id === usage.technology.id ? null : usage.technology">

              <div v-if="selectedTechnology?.id === usage.technology.id" class="w-full text-xs">
                <h3 class="font-semibold text-gray-500 dark:text-white text-sm mb-2">
                  {{ usage.technology.name }}
                </h3>

                <div v-for="productTechnology in usage.productTechnologies" class="flex items-center justify-between">
                  <template v-if="selectedBusinessUnit.key.length > 0">
                    <div>
                      <template v-if="productTechnology.comment.length > 0">{{ productTechnology.comment }}</template>
                      <span v-else class="text-gray-700 dark:text-gray-300">no comment</span>
                    </div>
                    <div v-if="productTechnology.version.length > 0" class="absolute right-2 top-2">
                      version {{ productTechnology.version }}
                    </div>
                  </template>
                  <template v-else>
                    <div>
                      <span class="text-gray-700 dark:text-gray-300">{{ productTechnology.expand?.product?.name }}</span>
                      by {{ productTechnology.expand?.product?.expand?.businessUnit?.name }}
                    </div>
                    <div>
                      <span v-if="productTechnology.version.length > 0" class="font-bold">v {{ productTechnology.version }}</span>
                    </div>
                  </template>
                </div>

                <hr class="my-2 border-gray-300 dark:border-gray-700">

                <router-link class="default-anchor" :to="{ name: 'technology', params: { id: usage.technology.id }}">
                  Details
                </router-link>
              </div>

              <div v-else class="flex flex-col items-center justify-center">
                <template v-if="usage.technology.simpleicon">
                  <img :src="`https://cdn.simpleicons.org/${usage.technology.simpleicon}`"
                       :alt="`Logo of ${usage.technology.name}`"
                       class="mb-2 w-12 h-12 object-contain"/>
                </template>
                <template v-else>
                  <img v-if="usage.technology.logo" :src="getLogoUrl(usage.technology)"
                       :alt="`Logo of ${usage.technology.name}`"
                       class="mb-1 w-10 h-10 object-contain"/>
                </template>

                <h3 class="font-semibold text-gray-500 dark:text-white text-sm text-center">
                  {{ usage.technology.name }}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Content>
</template>

